import React, { useState, useEffect, useRef } from "react";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Paper,
  ClickAwayListener,
  Checkbox,
  Divider,
} from "@mui/material";

import { ReactComponent as ChevronUp } from "assets/images/icons/icn_pagination_chevron_up.svg";
import { ReactComponent as ChevronDown } from "assets/images/icons/icn_pagination_chevron_down.svg";
import { ReactComponent as CheckDefIcon } from "assets/images/icons/checkbox_default.svg";
import { ReactComponent as CheckClearIcon } from "assets/images/icons/checkbox_clear.svg";
import { ReactComponent as CheckFillIcon } from "assets/images/icons/checkbox_fiilled.svg";

// Components
import { SolidButton } from "components/buttons/SolidButton";
import { OutlineButton } from "components/buttons/OutlineButton";

const TransactionTypeDropdown = ({
  selectedTypes,
  onChange,
  options,
  totalCount,
}) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [tempSelected, setTempSelected] = useState(selectedTypes);
  const containerRef = useRef(null);

  useEffect(() => {
    if (open) {
      setTempSelected(selectedTypes);
    }
  }, [open, selectedTypes]);

  const handleToggle = () => setOpen(!open);
  const handleClose = () => setOpen(false);

  const handleSelect = (value) => {
    if (value === "all") {
      if (tempSelected.length === options.length) {
        setTempSelected([]);
      } else {
        setTempSelected(options.map((opt) => opt.value));
      }
      return;
    }

    if (tempSelected.includes(value)) {
      setTempSelected(tempSelected.filter((v) => v !== value));
    } else {
      setTempSelected([...tempSelected, value]);
    }
  };

  const handleApply = () => {
    onChange(tempSelected);
    handleClose();
  };

  const handleReset = () => {
    setTempSelected(options.map((opt) => opt.value));
  };

  const getDisplayText = () => {
    if (selectedTypes.length === options.length) {
      return `All (${totalCount})`;
    }
    if (selectedTypes.length === 1) {
      const opt = options.find((o) => o.value === selectedTypes[0]);
      return `${opt?.name} (${opt?.tabCount || 0})`;
    }
    if (selectedTypes.length === 0) {
      return `None selected (0)`;
    }

    const selectedTotal = options
      .filter((opt) => selectedTypes.includes(opt.value))
      .reduce((sum, opt) => sum + (opt.tabCount || 0), 0);

    return `${selectedTypes.length} Types Selected (${selectedTotal})`;
  };

  const allSelected = tempSelected.length === options.length;
  const isIndeterminate =
    tempSelected.length > 0 && tempSelected.length < options.length;

  const iconStyle = { width: "25px", height: "25px" };

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Box sx={{ position: "relative" }} ref={containerRef}>
        <Box
          onClick={handleToggle}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            border: `1px solid ${theme.palette.primary.borderGreyPrimary}`,
            borderRadius: "8px",
            padding: "0 12px",
            width: "220px",
            height: "32px",
            cursor: "pointer",
            backgroundColor: "white",
            transition: "border-color 0.2s",
            "&:hover": {
              borderColor: theme.palette.primary.main,
            },
            ...(open && {
              borderColor: theme.palette.primary.main,
            }),
          }}
        >
          <Typography
            sx={{
              fontSize: "13px",
              fontWeight: 500,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              marginRight: "8px",
            }}
          >
            {getDisplayText()}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {open ? <ChevronUp /> : <ChevronDown />}
          </Box>
        </Box>

        {open && (
          <Paper
            sx={{
              position: "absolute",
              top: "36px",
              left: 0,
              width: "220px",
              zIndex: 100,
              borderRadius: "8px",
              boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
              padding: "4px 0",
              overflow: "hidden",
            }}
          >
            <List sx={{ padding: 0, maxHeight: "300px", overflowY: "auto" }}>
              <ListItem
                button
                onClick={() => handleSelect("all")}
                sx={{
                  padding: "6px 12px",
                  "&:hover": {
                    backgroundColor: "#F0F2F8",
                  },
                }}
              >
                <Checkbox
                  checked={allSelected}
                  indeterminate={isIndeterminate}
                  checkedIcon={<CheckFillIcon style={iconStyle} />}
                  icon={<CheckDefIcon style={iconStyle} />}
                  indeterminateIcon={<CheckClearIcon style={iconStyle} />}
                  sx={{
                    padding: 0,
                    mr: 1,
                    "&.Mui-checked, &.MuiCheckbox-indeterminate": {
                      color: "#172664",
                    },
                  }}
                />
                <ListItemText
                  primary={`All (${totalCount})`}
                  primaryTypographyProps={{
                    fontSize: "13px",
                    fontWeight: 600,
                    color: "#172664",
                  }}
                />
              </ListItem>
              <Divider sx={{ margin: "4px 0" }} />
              {options.map((opt) => (
                <ListItem
                  key={opt.value}
                  button
                  onClick={() => handleSelect(opt.value)}
                  sx={{
                    padding: "6px 12px",
                    "&:hover": {
                      backgroundColor: "#F0F2F8",
                    },
                  }}
                >
                  <Checkbox
                    checked={tempSelected.includes(opt.value)}
                    checkedIcon={<CheckFillIcon style={iconStyle} />}
                    icon={<CheckDefIcon style={iconStyle} />}
                    sx={{
                      padding: 0,
                      mr: 1,
                      "&.Mui-checked": {
                        color: "#172664",
                      },
                    }}
                  />
                  <ListItemText
                    primary={`${opt.name} (${opt.tabCount || 0})`}
                    primaryTypographyProps={{
                      fontSize: "13px",
                      color: "#172664",
                    }}
                  />
                </ListItem>
              ))}
            </List>
            <Divider sx={{ margin: "4px 0" }} />
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                padding: "12px",
                gap: 2,
              }}
            >
              <OutlineButton
                onClick={handleReset}
                width='96px'
                height='32px'
                sx={{
                  borderRadius: "20px",
                  color: "#172664",
                  borderColor: "#172664",
                  fontSize: "13px",
                  fontWeight: 600,
                  backgroundColor: "transparent",
                  "&:hover": {
                    borderColor: "#23367d",
                    color: "#23367d",
                    backgroundColor: "rgba(23, 38, 100, 0.04)",
                  },
                }}
              >
                Reset
              </OutlineButton>
              <SolidButton
                onClick={handleApply}
                width='96px'
                height='32px'
                sx={{
                  borderRadius: "20px",
                  bgcolor: "#172664",
                  fontSize: "13px",
                  fontWeight: 600,
                  boxShadow: "none",
                  "&:hover": {
                    bgcolor: "#23367d",
                    boxShadow: "none",
                  },
                }}
              >
                Apply
              </SolidButton>
            </Box>
          </Paper>
        )}
      </Box>
    </ClickAwayListener>
  );
};

export default TransactionTypeDropdown;
